/**
 * Workflow Editor Store - Dual Layer Structure
 *
 * Zustand store for workflow editor state management.
 * Uses new dual-layer structure: config (portable) + canvas (UI state)
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  type Connection,
  type NodeChange,
  type EdgeChange,
} from '@xyflow/react';
import type {
  NodeData,
  NodeType,
  PromptStrategy,
  PromptConfig,
  StorageMode,
  StepStatus,
  ExecutionStatus,
  WorkflowReactFlowNode,
  WorkflowReactFlowEdge,
  WorkflowViewport,
  WorkflowDefinition,
  Workflow,
  ExecutionStep,
} from '@/types/imageWorkflow';
import {
  definitionToReactFlow,
  reactFlowToDefinition,
  generateNodeKey,
  keyToReactFlowId,
  reactFlowIdToKey,
} from '@/types/imageWorkflow';

// ============================================================================
// Types
// ============================================================================

interface EditorSettings {
  promptStrategy: PromptStrategy;
  promptConfig: PromptConfig;
  storageMode: StorageMode;
}

/** Execution state for real-time visualization */
interface ExecutionState {
  executionId: string | null;
  status: ExecutionStatus | null;
  currentNodeKey: string | null;
  nodeStatuses: Record<string, StepStatus>;  // nodeKey -> status
  nodeResults: Record<string, ExecutionStep>;  // nodeKey -> result
  startedAt: string | null;
  completedAt: string | null;
  error: string | null;
}

/** History state for viewing past executions */
interface HistoryState {
  selectedExecutionId: string | null;
  selectedNodeKey: string | null;
  selectedExecutionSteps: ExecutionStep[];
  isViewingHistory: boolean;
}

/** Exported workflow JSON format for import/export */
export interface WorkflowExportData {
  metadata: {
    id?: string;
    key: string;
    name: string;
    description?: string;
    version?: number;
  };
  settings: {
    prompt_strategy: PromptStrategy;
    prompt_config: PromptConfig;
    storage_mode: StorageMode;
  };
  definition: {
    nodes: Array<{
      id: string;
      type: string;
      position: { x: number; y: number };
      data: Record<string, unknown>;
    }>;
    edges: Array<{
      id: string;
      source: string;
      target: string;
      sourceHandle?: string;
      targetHandle?: string;
    }>;
    viewport?: WorkflowViewport;
  };
}

interface WorkflowEditorStore {
  // ============================================================
  // Workflow Metadata
  // ============================================================
  workflowId: string | null;
  workflowKey: string;  // Unique key for API reference
  workflowName: string;
  workflowDescription: string;
  version: number;
  isDirty: boolean;

  // ============================================================
  // React Flow State
  // ============================================================
  nodes: WorkflowReactFlowNode[];
  edges: WorkflowReactFlowEdge[];
  viewport: WorkflowViewport;

  // ============================================================
  // Editor UI State
  // ============================================================
  selectedNodeId: string | null;  // ReactFlow ID (rf-{key})
  isEditorReady: boolean;
  settings: EditorSettings;

  // ============================================================
  // Execution State
  // ============================================================
  execution: ExecutionState;

  // ============================================================
  // History State
  // ============================================================
  history: HistoryState;

  // ============================================================
  // Actions - Workflow Management
  // ============================================================
  initNewWorkflow: () => void;
  loadWorkflow: (workflow: Workflow) => void;
  importFromJson: (jsonData: WorkflowExportData) => void;
  getDefinition: () => WorkflowDefinition;
  setWorkflowKey: (key: string) => void;
  setWorkflowName: (name: string) => void;
  setWorkflowDescription: (description: string) => void;
  markClean: () => void;

  // ============================================================
  // Actions - React Flow
  // ============================================================
  onNodesChange: (changes: NodeChange<WorkflowReactFlowNode>[]) => void;
  onEdgesChange: (changes: EdgeChange<WorkflowReactFlowEdge>[]) => void;
  onConnect: (connection: Connection) => void;
  setViewport: (viewport: WorkflowViewport) => void;

  // ============================================================
  // Actions - Node Management
  // ============================================================
  addNode: (type: NodeType, position?: { x: number; y: number }) => string;
  updateNodeData: (nodeId: string, data: Partial<NodeData>) => void;
  deleteNode: (nodeId: string) => void;
  duplicateNode: (nodeId: string) => string | null;
  selectNode: (nodeId: string | null) => void;

  // ============================================================
  // Actions - Edge Management
  // ============================================================
  deleteEdge: (edgeId: string) => void;

  // ============================================================
  // Actions - Settings
  // ============================================================
  setSettings: (settings: Partial<EditorSettings>) => void;
  setEditorReady: (ready: boolean) => void;

  // ============================================================
  // Actions - Execution
  // ============================================================
  startExecution: (executionId: string) => void;
  setNodeExecutionStatus: (nodeKey: string, status: StepStatus) => void;
  setNodeExecutionResult: (nodeKey: string, result: ExecutionStep) => void;
  completeExecution: (status: ExecutionStatus, error?: string) => void;
  clearExecution: () => void;
  getNodeResult: (nodeKey: string) => ExecutionStep | null;

  // ============================================================
  // Actions - History
  // ============================================================
  applyExecutionToCanvas: (steps: ExecutionStep[]) => void;
  selectExecution: (executionId: string | null) => void;
  selectHistoryNode: (nodeKey: string | null) => void;
  clearHistoryView: () => void;
  getHistoryNodeStep: (nodeKey: string) => ExecutionStep | null;

  // ============================================================
  // Actions - Reset
  // ============================================================
  reset: () => void;
}

// ============================================================================
// Default Values
// ============================================================================

const defaultSettings: EditorSettings = {
  promptStrategy: 'agent',
  promptConfig: {},
  storageMode: 'temp',
};

const defaultViewport: WorkflowViewport = {
  x: 0,
  y: 0,
  zoom: 1,
};

// Node label templates
const nodeLabels: Record<NodeType, string> = {
  start: 'Start',
  prompt: 'Prompt',
  generate: 'Generate Image',
  edit: 'Edit Image',
  fuse: 'Fuse Images',
  lucky_charm: 'Lucky Charm',
  push: 'Push',
  output: 'Output',
  read_today_artifact: 'Read Today Artifact',
};

const defaultExecutionState: ExecutionState = {
  executionId: null,
  status: null,
  currentNodeKey: null,
  nodeStatuses: {},
  nodeResults: {},
  startedAt: null,
  completedAt: null,
  error: null,
};

const defaultHistoryState: HistoryState = {
  selectedExecutionId: null,
  selectedNodeKey: null,
  selectedExecutionSteps: [],
  isViewingHistory: false,
};

// ============================================================================
// Helper Functions
// ============================================================================

/** Get all existing node keys from React Flow nodes */
function getExistingKeys(nodes: WorkflowReactFlowNode[]): string[] {
  return nodes.map((n) => n.data.nodeKey || reactFlowIdToKey(n.id));
}

/** Create a default start node */
function createDefaultStartNode(): WorkflowReactFlowNode {
  const key = 'start';
  return {
    id: keyToReactFlowId(key),
    type: 'start',
    position: { x: 100, y: 200 },
    data: {
      nodeKey: key,
      label: nodeLabels.start,
      enabled_builtins: ['user_id', 'user_name', 'timestamp'],
    },
  };
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useWorkflowStore = create<WorkflowEditorStore>()(
  immer((set, get) => ({
    // Initial state
    workflowId: null,
    workflowKey: '',
    workflowName: 'New Workflow',
    workflowDescription: '',
    version: 0,
    isDirty: false,

    nodes: [],
    edges: [],
    viewport: defaultViewport,

    selectedNodeId: null,
    isEditorReady: false,
    settings: defaultSettings,
    execution: defaultExecutionState,
    history: defaultHistoryState,

    // ============================================================
    // Workflow Management
    // ============================================================

    initNewWorkflow: () =>
      set((state) => {
        const startNode = createDefaultStartNode();
        state.workflowId = null;
        state.workflowKey = '';
        state.workflowName = 'New Workflow';
        state.workflowDescription = '';
        state.version = 0;
        state.isDirty = false;
        state.nodes = [startNode];
        state.edges = [];
        state.viewport = defaultViewport;
        state.selectedNodeId = null;
        state.settings = defaultSettings;
        state.execution = defaultExecutionState;
        state.history = defaultHistoryState;
      }),

    loadWorkflow: (workflow) =>
      set((state) => {
        const flowState = definitionToReactFlow(workflow.definition);
        state.workflowId = workflow.id;
        state.workflowKey = workflow.key;
        state.workflowName = workflow.name;
        state.workflowDescription = workflow.description ?? '';
        state.version = workflow.version;
        state.isDirty = false;
        state.nodes = flowState.nodes;
        state.edges = flowState.edges;
        state.viewport = flowState.viewport;
        state.selectedNodeId = null;
        state.settings = {
          promptStrategy: workflow.prompt_strategy,
          promptConfig: workflow.prompt_config,
          storageMode: workflow.default_storage_mode,
        };
        state.execution = defaultExecutionState;
        state.history = defaultHistoryState;
      }),

    importFromJson: (jsonData) =>
      set((state) => {
        // Build positions map from node positions
        const positions: Record<string, { x: number; y: number }> = {};
        for (const node of jsonData.definition.nodes) {
          positions[node.id] = node.position;
        }

        // Convert exported JSON format to internal WorkflowDefinition format
        const definition: WorkflowDefinition = {
          config: {
            nodes: jsonData.definition.nodes.map((n) => ({
              key: n.id,  // Use id as key for compatibility
              type: n.type as NodeType,
              data: n.data as NodeData,
            })),
            edges: jsonData.definition.edges.map((e) => ({
              key: e.id,
              from: e.source,
              to: e.target,
              fromHandle: e.sourceHandle,
              toHandle: e.targetHandle,
            })),
          },
          canvas: {
            positions,
            viewport: jsonData.definition.viewport ?? defaultViewport,
          },
        };

        const flowState = definitionToReactFlow(definition);

        // Clear workflow ID to force creating a new workflow on save
        state.workflowId = null;
        state.workflowKey = jsonData.metadata.key;
        state.workflowName = jsonData.metadata.name;
        state.workflowDescription = jsonData.metadata.description ?? '';
        state.version = 0;  // Reset version for new workflow
        state.isDirty = true;  // Mark as dirty since this is imported
        state.nodes = flowState.nodes;
        state.edges = flowState.edges;
        state.viewport = flowState.viewport;
        state.selectedNodeId = null;
        state.settings = {
          promptStrategy: jsonData.settings.prompt_strategy,
          promptConfig: jsonData.settings.prompt_config,
          storageMode: jsonData.settings.storage_mode,
        };
        state.execution = defaultExecutionState;
        state.history = defaultHistoryState;
      }),

    getDefinition: () => {
      const state = get();
      return reactFlowToDefinition({
        nodes: state.nodes,
        edges: state.edges,
        viewport: state.viewport,
      });
    },

    setWorkflowKey: (key) =>
      set((state) => {
        state.workflowKey = key;
        state.isDirty = true;
      }),

    setWorkflowName: (name) =>
      set((state) => {
        state.workflowName = name;
        state.isDirty = true;
      }),

    setWorkflowDescription: (description) =>
      set((state) => {
        state.workflowDescription = description;
        state.isDirty = true;
      }),

    markClean: () =>
      set((state) => {
        state.isDirty = false;
      }),

    // ============================================================
    // React Flow Actions
    // ============================================================

    onNodesChange: (changes) =>
      set((state) => {
        // Filter out remove changes for Start nodes
        const filteredChanges = changes.filter((change) => {
          if (change.type === 'remove') {
            const node = state.nodes.find((n) => n.id === change.id);
            if (node?.type === 'start') {
              return false;
            }
          }
          return true;
        });

        state.nodes = applyNodeChanges(filteredChanges, state.nodes);

        const contentChanges = filteredChanges.filter(
          (c) => c.type === 'add' || c.type === 'remove' || c.type === 'position'
        );
        if (contentChanges.length > 0) {
          state.isDirty = true;
        }
      }),

    onEdgesChange: (changes) =>
      set((state) => {
        // Clean up all mappings referencing the removed edge's source node
        const removeChanges = changes.filter((c) => c.type === 'remove');
        for (const change of removeChanges) {
          const removedEdge = state.edges.find((e) => e.id === change.id);
          if (removedEdge) {
            const sourceKey = reactFlowIdToKey(removedEdge.source);
            const targetKey = reactFlowIdToKey(removedEdge.target);
            const targetNode = state.nodes.find(
              (n) => (n.data.nodeKey || reactFlowIdToKey(n.id)) === targetKey
            );
            if (targetNode?.data?.input_mappings) {
              targetNode.data.input_mappings = targetNode.data.input_mappings.filter(
                (m: { source_node_key?: string }) => m.source_node_key !== sourceKey
              );
            }
          }
        }

        state.edges = applyEdgeChanges(changes, state.edges);
        const contentChanges = changes.filter(
          (c) => c.type === 'add' || c.type === 'remove'
        );
        if (contentChanges.length > 0) {
          state.isDirty = true;
        }
      }),

    onConnect: (connection) =>
      set((state) => {
        state.edges = addEdge(connection, state.edges);
        state.isDirty = true;
      }),

    setViewport: (viewport) =>
      set((state) => {
        state.viewport = viewport;
      }),

    // ============================================================
    // Node Management
    // ============================================================

    addNode: (type, position) => {
      const state = get();

      // Prevent adding duplicate Start node
      if (type === 'start' && state.nodes.some((n) => n.type === 'start')) {
        return '';
      }

      // Generate unique key
      const existingKeys = getExistingKeys(state.nodes);
      const key = generateNodeKey(type, existingKeys);
      const id = keyToReactFlowId(key);
      const pos = position ?? { x: 100, y: 100 };

      set((state) => {
        const baseData: NodeData & { nodeKey: string } = {
          nodeKey: key,
          label: nodeLabels[type],
        };

        if (type === 'start') {
          baseData.enabled_builtins = ['user_id', 'user_name', 'timestamp'];
        } else if (type === 'prompt') {
          baseData.input_mode = 'manual';
        }

        const newNode: WorkflowReactFlowNode = {
          id,
          type,
          position: pos,
          data: baseData,
        };
        state.nodes.push(newNode);
        state.isDirty = true;
        state.selectedNodeId = id;
      });

      return id;
    },

    updateNodeData: (nodeId, data) =>
      set((state) => {
        const node = state.nodes.find((n) => n.id === nodeId);
        if (node) {
          node.data = { ...node.data, ...data };
          state.isDirty = true;
        }
      }),

    deleteNode: (nodeId) =>
      set((state) => {
        const node = state.nodes.find((n) => n.id === nodeId);
        if (node?.type === 'start') {
          return;
        }

        state.nodes = state.nodes.filter((n) => n.id !== nodeId);
        state.edges = state.edges.filter(
          (e) => e.source !== nodeId && e.target !== nodeId
        );
        if (state.selectedNodeId === nodeId) {
          state.selectedNodeId = null;
        }
        state.isDirty = true;
      }),

    duplicateNode: (nodeId) => {
      const state = get();
      const node = state.nodes.find((n) => n.id === nodeId);
      if (!node || node.type === 'start') return null;

      const existingKeys = getExistingKeys(state.nodes);
      const newKey = generateNodeKey(node.type as NodeType, existingKeys);
      const newId = keyToReactFlowId(newKey);
      const newPosition = {
        x: node.position.x + 50,
        y: node.position.y + 50,
      };

      set((state) => {
        const { nodeKey: _, ...restData } = node.data;
        const newNode: WorkflowReactFlowNode = {
          id: newId,
          type: node.type,
          position: newPosition,
          data: {
            ...restData,
            nodeKey: newKey,
            label: `${node.data.label} (Copy)`,
          },
        };
        state.nodes.push(newNode);
        state.isDirty = true;
        state.selectedNodeId = newId;
      });

      return newId;
    },

    selectNode: (nodeId) =>
      set((state) => {
        state.selectedNodeId = nodeId;
      }),

    // ============================================================
    // Edge Management
    // ============================================================

    deleteEdge: (edgeId) =>
      set((state) => {
        // Find the edge before removing it so we can clean up stale mappings
        const removedEdge = state.edges.find((e) => e.id === edgeId);
        state.edges = state.edges.filter((e) => e.id !== edgeId);

        if (removedEdge) {
          const sourceKey = reactFlowIdToKey(removedEdge.source);
          const targetKey = reactFlowIdToKey(removedEdge.target);
          // Remove all mappings in the target node that reference the disconnected source
          const targetNode = state.nodes.find(
            (n) => (n.data.nodeKey || reactFlowIdToKey(n.id)) === targetKey
          );
          if (targetNode?.data?.input_mappings) {
            targetNode.data.input_mappings = targetNode.data.input_mappings.filter(
              (m: { source_node_key?: string }) => m.source_node_key !== sourceKey
            );
          }
        }

        state.isDirty = true;
      }),

    // ============================================================
    // Settings
    // ============================================================

    setSettings: (newSettings) =>
      set((state) => {
        state.settings = { ...state.settings, ...newSettings };
        state.isDirty = true;
      }),

    setEditorReady: (ready) =>
      set((state) => {
        state.isEditorReady = ready;
      }),

    // ============================================================
    // Execution
    // ============================================================

    startExecution: (executionId) =>
      set((state) => {
        state.execution = {
          executionId,
          status: 'running',
          currentNodeKey: null,
          nodeStatuses: {},
          nodeResults: {},
          startedAt: new Date().toISOString(),
          completedAt: null,
          error: null,
        };
        // Initialize all nodes as pending using their keys
        state.nodes.forEach((node) => {
          const key = node.data.nodeKey || reactFlowIdToKey(node.id);
          state.execution.nodeStatuses[key] = 'pending';
        });
      }),

    setNodeExecutionStatus: (nodeKey, status) =>
      set((state) => {
        state.execution.nodeStatuses[nodeKey] = status;
        if (status === 'running') {
          state.execution.currentNodeKey = nodeKey;
        } else if (status === 'completed' || status === 'failed') {
          if (state.execution.currentNodeKey === nodeKey) {
            state.execution.currentNodeKey = null;
          }
        }
      }),

    setNodeExecutionResult: (nodeKey, result) =>
      set((state) => {
        state.execution.nodeResults[nodeKey] = result;
        state.execution.nodeStatuses[nodeKey] = result.status;
      }),

    completeExecution: (status, error) =>
      set((state) => {
        state.execution.status = status;
        state.execution.completedAt = new Date().toISOString();
        state.execution.currentNodeKey = null;
        if (error) {
          state.execution.error = error;
        }
      }),

    clearExecution: () =>
      set((state) => {
        state.execution = defaultExecutionState;
      }),

    getNodeResult: (nodeKey) => {
      const state = get();
      return state.execution.nodeResults[nodeKey] ?? null;
    },

    // ============================================================
    // History
    // ============================================================

    applyExecutionToCanvas: (steps) =>
      set((state) => {
        state.execution.nodeStatuses = {};
        state.execution.nodeResults = {};

        steps.forEach((step) => {
          const key = step.node_key;
          if (key) {
            state.execution.nodeStatuses[key] = step.status;
            state.execution.nodeResults[key] = step;
          }
        });

        state.history.selectedExecutionSteps = steps;
        state.history.isViewingHistory = true;
      }),

    selectExecution: (executionId) =>
      set((state) => {
        state.history.selectedExecutionId = executionId;
        state.history.selectedNodeKey = null;
        if (!executionId) {
          state.history.isViewingHistory = false;
          state.history.selectedExecutionSteps = [];
          state.execution.nodeStatuses = {};
          state.execution.nodeResults = {};
        }
      }),

    selectHistoryNode: (nodeKey) =>
      set((state) => {
        state.history.selectedNodeKey = nodeKey;
      }),

    clearHistoryView: () =>
      set((state) => {
        state.history = defaultHistoryState;
        state.execution.nodeStatuses = {};
        state.execution.nodeResults = {};
      }),

    getHistoryNodeStep: (nodeKey) => {
      const state = get();
      return state.history.selectedExecutionSteps.find((s) => s.node_key === nodeKey) ?? null;
    },

    // ============================================================
    // Reset
    // ============================================================

    reset: () =>
      set((state) => {
        const startNode = createDefaultStartNode();
        state.workflowId = null;
        state.workflowKey = '';
        state.workflowName = 'New Workflow';
        state.workflowDescription = '';
        state.version = 0;
        state.isDirty = false;
        state.nodes = [startNode];
        state.edges = [];
        state.viewport = defaultViewport;
        state.selectedNodeId = null;
        state.isEditorReady = false;
        state.settings = defaultSettings;
        state.execution = defaultExecutionState;
        state.history = defaultHistoryState;
      }),
  }))
);

// ============================================================================
// Selectors
// ============================================================================

/** Get the currently selected node */
export const useSelectedNode = () => {
  const nodes = useWorkflowStore((s) => s.nodes);
  const selectedNodeId = useWorkflowStore((s) => s.selectedNodeId);
  return nodes.find((n) => n.id === selectedNodeId) ?? null;
};

/** Check if the workflow has unsaved changes */
export const useIsDirty = () => useWorkflowStore((s) => s.isDirty);

/** Get workflow metadata */
export const useWorkflowMetadata = () =>
  useWorkflowStore((s) => ({
    id: s.workflowId,
    key: s.workflowKey,
    name: s.workflowName,
    description: s.workflowDescription,
    version: s.version,
  }));

/** Get editor settings */
export const useEditorSettings = () => useWorkflowStore((s) => s.settings);

/** Get node count by type */
export const useNodeCounts = () => {
  const nodes = useWorkflowStore((s) => s.nodes);
  return {
    generate: nodes.filter((n) => n.type === 'generate').length,
    edit: nodes.filter((n) => n.type === 'edit').length,
    fuse: nodes.filter((n) => n.type === 'fuse').length,
    output: nodes.filter((n) => n.type === 'output').length,
    total: nodes.length,
  };
};

/** Get execution state */
export const useExecutionState = () => useWorkflowStore((s) => s.execution);

/** Check if workflow is currently executing */
export const useIsExecuting = () =>
  useWorkflowStore((s) => s.execution.status === 'running');

/** Get node execution status by key */
export const useNodeExecutionStatus = (nodeKey: string) =>
  useWorkflowStore((s) => s.execution.nodeStatuses[nodeKey] ?? null);

/** Get all node statuses for rendering */
export const useAllNodeStatuses = () =>
  useWorkflowStore((s) => s.execution.nodeStatuses);

/** Get current executing node key */
export const useCurrentExecutingNode = () =>
  useWorkflowStore((s) => s.execution.currentNodeKey);

/** Get node execution result by key */
export const useNodeExecutionResult = (nodeKey: string) =>
  useWorkflowStore((s) => s.execution.nodeResults[nodeKey] ?? null);

// ============================================================================
// History Selectors
// ============================================================================

/** Get history state */
export const useHistoryState = () => useWorkflowStore((s) => s.history);

/** Get selected execution ID */
export const useSelectedExecutionId = () =>
  useWorkflowStore((s) => s.history.selectedExecutionId);

/** Get selected node key for history viewing */
export const useSelectedHistoryNodeKey = () =>
  useWorkflowStore((s) => s.history.selectedNodeKey);

/** Check if viewing history mode */
export const useIsViewingHistory = () =>
  useWorkflowStore((s) => s.history.isViewingHistory);

/** Get selected execution steps */
export const useSelectedExecutionSteps = () =>
  useWorkflowStore((s) => s.history.selectedExecutionSteps);

/** Get step data for currently selected history node */
export const useSelectedHistoryNodeStep = () => {
  const selectedNodeKey = useWorkflowStore((s) => s.history.selectedNodeKey);
  const steps = useWorkflowStore((s) => s.history.selectedExecutionSteps);
  if (!selectedNodeKey) return null;
  return steps.find((s) => s.node_key === selectedNodeKey) ?? null;
};
